import type { RootState } from '@/store';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { ConsultNoteStatus, RecordingStatus } from '@heidi/schema/ml-scribe';
import { useQuerySessionById } from '@heidi/session/data';
import { useSelector } from 'react-redux';

export type SessionStage = 'pre' | 'during' | 'post';

/**
 * Determines the current session stage from note and recording state.
 *
 * - pre: No active recording state and note is still in CREATED
 * - during: Recording is active (running timer, RECORDING, or PAUSED)
 * - post: Note generation has started or completed (IN_PROGRESS or COMPLETED)
 */
export function useSessionStage(): SessionStage {
  const sessionId = useCurrentSessionId();
  const isRunning = useSelector((state: RootState) => state.timerModel.isRunning);

  const { data: sessionData } = useQuerySessionById({
    sessionId: sessionId ?? '',
    select: (data) => ({
      noteStatus: data?.consult_note?.status,
      recordingStatus: data?.recording_status,
    }),
  });

  const hasNoteGenerated = sessionData?.noteStatus === ConsultNoteStatus.COMPLETED;
  const isNoteGenerating = sessionData?.noteStatus === ConsultNoteStatus.IN_PROGRESS;
  const isRecordingActive =
    sessionData?.recordingStatus === RecordingStatus.RECORDING ||
    sessionData?.recordingStatus === RecordingStatus.PAUSED;

  if (hasNoteGenerated || isNoteGenerating) return 'post';
  if (isRunning || isRecordingActive) return 'during';
  return 'pre';
}
